import React from 'react';
import {useLocation} from "react-router-dom";
import shortid from "shortid";
import {
    Menu,
    MenuItem,
} from '@szhsin/react-menu';
import '@szhsin/react-menu/dist/index.css';
import {useAsync} from "react-async-hook";
import {roomApi} from "../apis/admin/room/roomApi";
import useLoading from '../hooks/useLoading';
import {useAuth} from "../hooks/useAuth";
import {config} from "../libs/config/config";
import {PasswordEditModal} from "../components/pages/admin/Account/PasswordEditModal";
import {logError} from "../libs/utils/logError";
import useAppRouter from "../hooks/useAppRouter";


interface Room {
  id: number,
  name: string,
  content: string
}

const zeroPad2Digits = new Intl.NumberFormat('ja', { minimumIntegerDigits: 2 });

export const RoomSelectPage: React.FC = () => {
  const [abortController, setAbortController] = React.useState<AbortController | undefined>();
  const [roomList, setRoomList] = React.useState<Room[]>([]);
  const withLoading = useLoading();
  const location = useLocation();
  const { goto } = useAppRouter();

  const { logout } = useAuth();

  const [ showPasswordEditModal, setShowPasswordEditModal ] = React.useState<boolean>(false);

  useAsync(async () => {
    const fetch = async () => {
      withLoading(async () => {
      setAbortController(new AbortController());
        try
        {
          const response = await roomApi.index({ abortSignal: abortController?.signal });
          setRoomList(response.rooms);
        } catch(e) {
          await logError(
            location.pathname,
            'ROOM一覧の取得に失敗しました。',
            e
          );

          goto('internalServerError');
        }
      });
    };
    fetch();
  }, []);

  const handleClick = async (id: number) => {
    withLoading(async () => {
      try {
        /*
        await roomApi.select({
          roomId: id
        }, {});
        if (config.room.page.release.url === undefined) {
          await logError(
            location.pathname,
            'ROOMのURLが設定されていません。',
          );
          goto('internalServerError');
          return;
        }
        */
        window.location.href = `${config.room.page.release.url}?roomId=${id}`;

      } catch (e)  {
        await logError(
          location.pathname,
          'ROOMの切り替えに失敗しました。',
          e
        );

        goto('internalServerError');
      }
    });
  };


  return (
    <>
      {showPasswordEditModal && (
        <PasswordEditModal
          {...{
            setShowModal: setShowPasswordEditModal,
            onSaved: async () => {setShowPasswordEditModal(false)},
          }}
        />
      )}

      <div className="bg-primary">
        <header className="flex items-center justify-between p-4 h-30">
          <div className="flex-1 min-w-0 ml-4">
            <span className="text-4xl font-extrabold text-white">ROOM</span>
          </div>
          <div className="block">
            <div className="flex items-center ml-4">
              <div className="ml-3 relative">
                <div className="relative inline-block text-left">
                  <Menu
                    position="anchor"
                    align="end"
                    /* eslint-disable-next-line react/jsx-boolean-value */
                    arrow={true}
                    menuButton={
                      <button 
                        type="button"
                        className="flex items-center justify-center w-full rounded-md  px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-50 focus:outline-none"
                        >
                        <svg width="20" fill="currentColor" height="20" className="text-white"
                             viewBox="0 0 1792 1792" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M1523 1339q-22-155-87.5-257.5t-184.5-118.5q-67 74-159.5 115.5t-195.5 41.5-195.5-41.5-159.5-115.5q-119 16-184.5 118.5t-87.5 257.5q106 150 271 237.5t356 87.5 356-87.5 271-237.5zm-243-699q0-159-112.5-271.5t-271.5-112.5-271.5 112.5-112.5 271.5 112.5 271.5 271.5 112.5 271.5-112.5 112.5-271.5zm512 256q0 182-71 347.5t-190.5 286-285.5 191.5-349 71q-182 0-348-71t-286-191-191-286-71-348 71-348 191-286 286-191 348-71 348 71 286 191 191 286 71 348z" />
                        </svg>
                      </button>
                    }
                  >
                    <MenuItem styles={{ padding: '0'}} className="hover:bg-blue-500 hover:text-white">
                      <button
                        className="block px-4 py-2 w-full text-sm text-left text-gray-700 hover:bg-blue-500 hover:text-white"
                        type="button"
                        onClick={() => {
                          setShowPasswordEditModal(true);
                        }}
                        >
                        パスワード変更
                      </button>
                    </MenuItem>
                    <MenuItem styles={{ padding: '0'}} className="hover:bg-blue-500 hover:text-white">
                      <button
                        className="block px-4 py-2 w-full text-sm text-left text-gray-700 hover:bg-blue-500 hover:text-white"
                        type="button"
                        onClick={() => withLoading(logout)}
                        >
                        ログアウト
                      </button>
                    </MenuItem>
                  </Menu>
                </div>
              </div>
            </div>
          </div>
        </header>

        <main>
          <div className="min-h-screen p-16">
          <div className="grid grid-cols-4 gap-4">
            
          {roomList.map((room, index) => (
            <div key={shortid.generate()} className="bg-white p-5">
              <div className="text-primary font-extrabold mb-2">
                <span className="text-4xl">ROOM</span>
                <span className="ml-1">#{zeroPad2Digits.format(index+1)}</span>
              </div>
              <div className="text-lg font-bold border-b border-primary py-1">
                {room.name}
              </div>
              <div className="mt-2 mb-4 text-sm">
                {room.content}
              </div>
              <div>
                <button
                  type="button"
                  className="bg-primary font-extrabold text-white w-full p-2"
                  onClick={() => handleClick(room.id)}
                  >
                  ENTER
                </button>
              </div>
            </div>
          ))}

          </div>
          </div>
        </main>
      </div>
    </>
  );
};
